import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  ArrowLeft, UserCheck, CheckCircle2, Clock, FileText, ChevronRight, Hourglass,
  Archive, Layout as LayoutIcon, FolderOpen, FileText as FileTextIcon, Database, MessageCircle,
  Activity as ActivityIcon, Palette as PaletteIcon
} from "lucide-react";
import StageDetailDrawer from "@/components/StageDetailDrawer";
import IdeaLinksPanel from "@/components/IdeaLinksPanel";
import CollaboratorsPanel from "@/components/CollaboratorsPanel";
import UserProfileLink from "@/components/UserProfileLink";
import ProjectChat from "@/components/ProjectChat";
import ParticipantsCard from "@/components/ParticipantsCard";
import ActiveCallBanner from "@/components/ActiveCallBanner";
import DrivePreviewSidebar from "@/components/DrivePreviewSidebar";
import { MeetingsBar, TeamPanel, KanbanBoard, PaperEditor, DriveTab } from "@/components/WorkspaceHub";
import { ProgressLogTab, DocsTab, WhiteboardTab, WorkspaceCollaboratorsPanel } from "@/pages/GuidanceWorkspace";
import { requestExtraScopes } from "@/lib/googleAuth";
import { useAuth } from "@/contexts/AuthContext";
import { useCall } from "@/contexts/CallContext";

const SCOPE_MEET = "openid email profile https://www.googleapis.com/auth/meetings.space.created";
const SCOPE_DRIVE = "openid email profile https://www.googleapis.com/auth/drive.file";

export default function IdeaDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [idea, setIdea] = useState(null);
  const [timeline, setTimeline] = useState(null);
  const [drawerStage, setDrawerStage] = useState(null);
  const [lifecycleOpen, setLifecycleOpen] = useState(false);
  const [participants, setParticipants] = useState([]);
  const [googleStatus, setGoogleStatus] = useState({ has_drive: false, has_meet: false });
  const [activeTab, setActiveTab] = useState("chat");
  const [activeMeeting, setActiveMeeting] = useState(null);
  const { startCall, endCall } = useCall();
  // Lightweight workspace WebSocket — powers real-time Docs, Whiteboard,
  // and future collab features. Shared with GuidanceWorkspace's pattern.
  const wsRef = useRef(null);
  const [wsConnected, setWsConnected] = useState(false);
  useEffect(() => {
    if (!idea?.id) return;
    const token = localStorage.getItem("kb_session_token");
    if (!token) return;
    const wsProtocol = window.location.protocol === "https:" ? "wss:" : "ws:";
    const backend = process.env.REACT_APP_BACKEND_URL || window.location.origin;
    const apiHost = backend.replace(/^https?:/, "");
    const ws = new WebSocket(`${wsProtocol}${apiHost}/api/ws/guidance/${idea.id}?token=${encodeURIComponent(token)}`);
    wsRef.current = ws;
    ws.onopen = () => setWsConnected(true);
    ws.onclose = () => setWsConnected(false);
    ws.onerror = () => setWsConnected(false);
    const ping = setInterval(() => { if (ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify({ type: "ping" })); }, 30000);
    return () => { clearInterval(ping); ws.close(); wsRef.current = null; };
  }, [idea?.id]);

  // Push active meeting into the persistent global call host so the iframe
  // survives route changes (Google-Meet-style behaviour).
  useEffect(() => {
    if (!activeMeeting) return;
    if (activeMeeting.provider === "google_meet") {
      startCall({ provider: "google_meet", meetingUri: activeMeeting.meeting_uri });
    } else {
      startCall({
        provider: "daily",
        roomUrl: activeMeeting.room_url,
        displayName: user?.name || user?.email || "Guest",
        audioOnly: !!activeMeeting.audio_only,
      });
    }
  }, [activeMeeting, startCall, user]);

  const load = async () => {
    const r1 = await api.get(`/ideas/${id}`);
    setIdea(r1.data);
    if (r1.data.status !== "Pending Admin Review" && r1.data.status !== "Rejected") {
      try {
        const r2 = await api.get(`/ideas/${id}/timeline`);
        setTimeline(r2.data);
      } catch (e) { /* timeline optional */ }
    }
  };
  // Timeline-only refresh — used after task toggles inside the stage drawer
  // so we don't re-fetch the whole idea + participants and cause the whole
  // right rail to blink.
  const refreshTimeline = async () => {
    try {
      const r = await api.get(`/ideas/${id}/timeline`);
      setTimeline(r.data);
    } catch (e) { /* silent */ }
  };
  useEffect(() => { load(); }, [id]);

  // Fetch workspace participants + Google status once idea is loaded & active
  useEffect(() => {
    if (!idea || idea.status === "Pending Admin Review" || idea.status === "Rejected") return;
    api.get(`/workspace/${idea.id}/participants`).then((r) => setParticipants(r.data || [])).catch(() => {});
    api.get(`/auth/google/status`).then((r) => setGoogleStatus(r.data || {})).catch(() => {});
  }, [idea?.id, idea?.status]);

  // In-chat audio/video buttons ALWAYS use Daily.co (in-app, KhojByte's own
  // subdomain). Google Meet has its own explicit entry in the MeetingsBar
  // ("Start now · Google Meet"). This split keeps the two technologies
  // clearly separated.
  const startInstantMeet = async (audioOnly = false) => {
    try {
      const r = await api.post(`/workspace/${idea.id}/call/daily-room?audio_only=${audioOnly}`);
      const room = r.data;
      setActiveMeeting({
        id: room.room_name,
        room_name: room.room_name,
        room_url: room.url,
        audio_only: audioOnly,
        provider: "daily",
      });
      try {
        await api.post(`/workspace/${idea.id}/call/start`, {
          provider: "daily",
          room_name: room.room_name,
          join_url: room.url,
          audio_only: audioOnly,
        });
        toast.success(`${audioOnly ? "Audio" : "Video"} call started · everyone in this workspace is being notified`);
      } catch {
        toast.success(`${audioOnly ? "Audio" : "Video"} call started (offline broadcast — other participants may need to refresh)`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't start Daily.co call");
    }
  };

  // Explicit Google Meet starter — used by MeetingsBar (kept here as a helper
  // so the same call-start broadcast logic runs).
  const startGoogleMeetCall = async (audioOnly = false) => {
    if (!googleStatus.has_meet) {
      await requestExtraScopes(SCOPE_MEET);
      return;
    }
    try {
      const r = await api.post("/google/meet/create", {
        purpose: audioOnly ? "audio_call" : "video_call",
        context_id: idea?.id,
      });
      setActiveMeeting({ ...r.data, audio_only: audioOnly, provider: "google_meet" });
      try {
        await api.post(`/workspace/${idea.id}/call/start`, {
          provider: "google_meet",
          room_name: r.data.meeting_code || r.data.id,
          join_url: r.data.meeting_uri,
          audio_only: audioOnly,
        });
      } catch { /* non-fatal */ }
      toast.success(audioOnly ? "Audio call started · Google Meet" : "Video call started · Google Meet");
    } catch (e) {
      if (e?.response?.status === 429) toast.error("Google Meet quota reached — try again in a minute.");
      else toast.error(e?.response?.data?.detail || "Couldn't start call");
    }
  };

  // Join an in-progress call started by another participant.
  // - Daily.co → mount EmbeddedMeetPanel with the SAME room URL (join_url is
  //   the canonical Daily room link broadcast on /call/start).
  // - Google Meet → opens the original Meet URL in a separate floating card.
  const joinActiveCall = async (callDoc) => {
    if (callDoc.provider === "daily" || (!callDoc.provider && callDoc.join_url?.includes(".daily.co"))) {
      // Backend may already have the URL on the active-call doc; if not, fetch.
      let url = callDoc.join_url;
      if (!url) {
        try {
          const r = await api.post(`/workspace/${idea.id}/call/daily-room?audio_only=${!!callDoc.audio_only}`);
          url = r.data.url;
        } catch (e) {
          toast.error(e?.response?.data?.detail || "Couldn't join the call");
          return;
        }
      }
      setActiveMeeting({
        id: callDoc.room_name,
        room_name: callDoc.room_name,
        room_url: url,
        audio_only: !!callDoc.audio_only,
        provider: "daily",
      });
      return;
    }
    setActiveMeeting({
      id: callDoc.room_name,
      room_name: callDoc.room_name,
      audio_only: !!callDoc.audio_only,
      provider: callDoc.provider || "google_meet",
      meeting_uri: callDoc.join_url,
    });
  };

  // Close panel locally and tell the backend so other participants stop seeing
  // the "Join call" banner once the initiator leaves.
  const closeCallPanel = async () => {
    setActiveMeeting(null);
    endCall();
    try { await api.post(`/workspace/${idea.id}/call/end`); } catch { /* ignore */ }
  };

  if (!idea) return <div className="kb-mono text-xs text-kb-ink-muted">Loading…</div>;

  const role = user?.role;
  const isPending = idea.status === "Pending Admin Review";
  const isRejected = idea.status === "Rejected";
  const isCompleted = idea.status === "Publication" || idea.status === "Rejected";
  const canArchive = isCompleted && !idea.is_archived && (user?.role === "admin" || idea.user_id === user?.user_id);

  const archive = async () => {
    if (!window.confirm("Archive this project? It will be hidden from your active list.")) return;
    try {
      await api.post(`/ideas/${idea.id}/archive`);
      toast.success("Archived");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div data-testid="idea-detail-page">
      <Link to={role === "admin" ? "/admin/projects" : (role === "mentor" || role === "tutor") ? "/mentor/projects" : "/dashboard/ideas"}
            className="kb-mono text-xs text-kb-ink-muted hover:text-kb-primary inline-flex items-center gap-2">
        <ArrowLeft className="w-3.5 h-3.5" /> BACK
      </Link>
      {(idea.is_archived || canArchive) && (
        <div className="mt-3 inline-flex items-center gap-3">
          {idea.is_archived && (
            <span className="kb-mono text-[10px] uppercase tracking-wider text-slate-500 bg-slate-100 border border-slate-200 px-2 py-1 rounded-sm inline-flex items-center gap-1">
              <Archive className="w-3 h-3" /> ARCHIVED
            </span>
          )}
          {canArchive && (
            <button onClick={archive} className="kb-btn-outline text-xs" data-testid="archive-idea-btn">
              <Archive className="w-3.5 h-3.5" /> Archive project
            </button>
          )}
        </div>
      )}

      <div className="mt-6 grid lg:grid-cols-12 gap-6">
        {/* ================== MAIN COLUMN ================== */}
        <div className="lg:col-span-9 space-y-5">
          {/* Title card — collapsed by default. Click to expand for description / metadata. */}
          <details className="kb-card" data-testid="title-card">
            <summary className="cursor-pointer list-none px-7 py-5 flex items-center justify-between gap-4">
              <div className="flex-1 min-w-0">
                <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted truncate">
                  {idea.id} · {idea.domain}{idea.category ? ` · ${idea.category}` : ""}
                </div>
                <h1 className="font-serif text-2xl text-kb-ink mt-1 leading-tight truncate">{idea.title}</h1>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {isPending && (
                  <span className="kb-mono text-[10px] uppercase tracking-wider text-amber-700 bg-amber-50 border border-amber-200 px-2 py-1 rounded-sm inline-flex items-center gap-1">
                    <Hourglass className="w-3 h-3" /> PENDING
                  </span>
                )}
                {isRejected && (
                  <span className="kb-mono text-[10px] uppercase tracking-wider text-rose-700 bg-rose-50 border border-rose-200 px-2 py-1 rounded-sm">REJECTED</span>
                )}
                {!isPending && !isRejected && (
                  <span className="kb-mono text-[10px] uppercase tracking-wider text-kb-primary bg-kb-primary/5 border border-kb-primary/20 px-2 py-1 rounded-sm">
                    {idea.status}
                  </span>
                )}
                <ChevronRight className="w-4 h-4 text-kb-ink-muted" />
              </div>
            </summary>
            <div className="px-7 pb-6">
              <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted">CLIENT</div>
              <div className="text-sm text-kb-ink mt-1"><UserProfileLink userId={idea.user_id} name={idea.user_name} /></div>

              {(idea.keywords || []).length > 0 && (
                <div className="mt-4 flex flex-wrap gap-2">
                  {(idea.keywords || []).map((k) => (
                    <span key={k} className="kb-mono text-[10px] uppercase tracking-wider text-kb-primary bg-kb-primary/5 border border-kb-primary/15 px-2 py-1 rounded-sm">{k}</span>
                  ))}
                </div>
              )}

              {!isPending && !isRejected && (
                <div className="mt-5 grid grid-cols-4 gap-px bg-slate-200 border border-slate-200 rounded-sm overflow-hidden">
                  <div className="bg-white p-3"><div className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted">Feasibility</div><div className="font-mono text-xl text-kb-primary mt-1">{idea.feasibility_score ?? "—"}<span className="text-sm text-kb-ink-muted">/100</span></div></div>
                  <div className="bg-white p-3"><div className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted">Category</div><div className="font-mono text-xs text-kb-ink mt-2">{idea.category || "—"}</div></div>
                  <div className="bg-white p-3"><div className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted">Priority</div><div className="font-mono text-xs text-kb-ink mt-2 capitalize">{idea.priority || "—"}</div></div>
                  <div className="bg-white p-3"><div className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted">Stage</div><div className="font-mono text-xs text-kb-ink mt-2">{idea.status}</div></div>
                </div>
              )}

              <div className="mt-5 pt-4 border-t border-slate-200">
                <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted">DESCRIPTION</div>
                <p className="text-kb-ink mt-3 leading-relaxed whitespace-pre-wrap">{idea.description || idea.abstract}</p>
                {idea.methodology && (
                  <>
                    <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted mt-5">METHODOLOGY</div>
                    <p className="text-kb-ink-muted mt-3 leading-relaxed whitespace-pre-wrap">{idea.methodology}</p>
                  </>
                )}
              </div>
            </div>
          </details>

          {/* Scheduled meetings + instant meet */}
          {!isPending && !isRejected && (
            <MeetingsBar workspaceId={idea.id} status={googleStatus} onConnectMeet={() => requestExtraScopes(SCOPE_MEET)} />
          )}

          {/* Active call banner — every participant in this workspace sees
              the same call notification and can join the SAME Jitsi room. */}
          {!isPending && !isRejected && (
            <ActiveCallBanner
              workspaceId={idea.id}
              currentUserId={user?.user_id}
              onJoin={joinActiveCall}
            />
          )}

          {/* Tabs: Chat (default) / Tasks / Progress / Docs / Whiteboard / Paper Draft / Drive Files */}
          {!isPending && !isRejected && (
            <div className="kb-card overflow-hidden">
              <div className="flex border-b border-slate-200 bg-slate-50 overflow-x-auto">
                {[
                  { key: "chat", label: "Project Chat", icon: MessageCircle },
                  { key: "tasks", label: "Tasks", icon: LayoutIcon },
                  { key: "progress", label: "Progress Log", icon: ActivityIcon },
                  { key: "docs", label: "Docs", icon: FileTextIcon },
                  { key: "whiteboard", label: "Whiteboard", icon: PaletteIcon },
                  { key: "paper", label: "Paper Draft", icon: FileTextIcon },
                  { key: "drive", label: "Drive Files", icon: FolderOpen },
                ].map((t) => {
                  const Icon = t.icon;
                  const active = activeTab === t.key;
                  return (
                    <button
                      key={t.key}
                      onClick={() => setActiveTab(t.key)}
                      className={`flex items-center gap-2 px-5 py-3 text-sm kb-mono tracking-wider uppercase text-[11px] border-b-2 whitespace-nowrap transition-colors ${
                        active ? "border-kb-primary text-kb-primary bg-white" : "border-transparent text-kb-ink-muted hover:text-kb-ink"
                      }`}
                      data-testid={`idea-tab-${t.key}`}
                    >
                      <Icon className="w-4 h-4" /> {t.label}
                    </button>
                  );
                })}
              </div>
              <div className={activeTab === "chat" ? "" : "p-5"}>
                {activeTab === "chat" && (
                  <ProjectChat
                    ideaId={idea.id}
                    onStartVideoCall={() => startInstantMeet(false)}
                    onStartAudioCall={() => startInstantMeet(true)}
                    statusLine={`${participants.length} participant${participants.length === 1 ? "" : "s"} · ${idea.status}`}
                  />
                )}
                {activeTab === "tasks" && <KanbanBoard workspaceId={idea.id} participants={participants} />}
                {activeTab === "progress" && (
                  <ProgressLogTab guidanceId={idea.id} currentUser={user} ownerId={idea.user_id} mentorUserId={idea.assigned_mentor_user_id} />
                )}
                {activeTab === "docs" && (
                  <DocsTab guidanceId={idea.id} currentUser={user} ownerId={idea.user_id} wsRef={wsRef} wsConnected={wsConnected} />
                )}
                {activeTab === "whiteboard" && (
                  <WhiteboardTab guidanceId={idea.id} wsRef={wsRef} wsConnected={wsConnected} currentUser={user} />
                )}
                {activeTab === "paper" && <PaperEditor workspaceId={idea.id} />}
                {activeTab === "drive" && (
                  <div>
                    <DriveTab status={googleStatus} onConnect={() => requestExtraScopes(SCOPE_DRIVE)} workspaceTitle={idea.title} workspaceId={idea.id} />
                    <div className="mt-6 pt-6 border-t border-slate-200">
                      <details open>
                        <summary className="cursor-pointer kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted hover:text-kb-primary inline-flex items-center gap-2">
                          <Database className="w-3 h-3" /> Public share links (datasets · code · papers)
                        </summary>
                        <div className="mt-4">
                          <IdeaLinksPanel idea={idea} onRefresh={load} />
                          {idea.dataset_link && (
                            <a href={idea.dataset_link} target="_blank" rel="noreferrer" className="block mt-3 text-kb-primary text-xs underline break-all">
                              Original submission link: {idea.dataset_link}
                            </a>
                          )}
                        </div>
                      </details>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* ================== SIDEBAR ================== */}
        <div className="lg:col-span-3 space-y-3">
          {/* MENTOR & COLLABORATORS — collapsed by default (per user preference) */}
          <details className="kb-card p-5" data-testid="mentor-collab-card">
            <summary className="cursor-pointer list-none flex items-center justify-between">
              <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted">MENTOR & COLLABORATORS · {participants.length}</div>
              <ChevronRight className="w-4 h-4 text-kb-ink-muted" />
            </summary>
            <div className="mt-3">
              {!isPending && !isRejected ? (
                <ParticipantsCard
                  participants={participants}
                  currentUserId={user?.user_id}
                  mentor={idea.assigned_mentor}
                  mentors={idea.co_mentors || []}
                  workspaceId={idea.id}
                  onParticipantsChanged={() => {
                    api.get(`/workspace/${idea.id}/participants`).then((r) => setParticipants(r.data || [])).catch(() => {});
                  }}
                  canManageMentors={user?.role === "admin" || user?.user_id === idea.mentor_user_id || user?.user_id === idea.user_id}
                  onAddCoMentor={async (m) => {
                    try { await api.post(`/guidance/${idea.id}/mentors`, { mentor_id: m.id }); await load(); }
                    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't add mentor"); }
                  }}
                  onRemoveCoMentor={async (mid) => {
                    if (!window.confirm("Remove this co-mentor?")) return;
                    try { await api.delete(`/guidance/${idea.id}/mentors/${mid}`); await load(); }
                    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't remove"); }
                  }}
                  onStartCall={(audio) => startInstantMeet(audio)}
                  onMessage={(p) => {
                    // Open the global Gmail-like DM widget on this thread.
                    window.dispatchEvent(new CustomEvent("kb-open-dm", { detail: { user: p } }));
                  }}
                />
              ) : (
                <p className="text-sm text-kb-ink-muted">
                  {isPending ? "Mentor will be assigned once feasibility is approved." : "Workspace not active."}
                </p>
              )}
              {!isPending && !isRejected && (
                <div className="mt-3 pt-3 border-t border-slate-200">
                  <CollaboratorsPanel ideaId={idea.id} currentUser={user} ownerUserId={idea.user_id} assignedMentorId={idea.assigned_mentor_id} />
                </div>
              )}
            </div>
          </details>

          {/* MANUAL SHARE LINKS — GitHub / Drive folder URLs entered by hand.
              The AUTHORIZED Drive (create/upload dynamically) lives in the
              Drive tab. */}
          {!isPending && !isRejected && (
            <details className="kb-card p-5">
              <summary className="cursor-pointer list-none flex items-center justify-between">
                <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted inline-flex items-center gap-1.5">
                  <Database className="w-3 h-3" /> MANUAL SHARE LINKS · GITHUB &middot; DRIVE URL
                </div>
                <ChevronRight className="w-4 h-4 text-kb-ink-muted" />
              </summary>
              <div className="mt-3">
                <IdeaLinksPanel idea={idea} onRefresh={load} />
                {idea.dataset_link && (
                  <a href={idea.dataset_link} target="_blank" rel="noreferrer" className="block mt-3 text-kb-primary text-xs underline break-all">
                    Original submission link: {idea.dataset_link}
                  </a>
                )}
              </div>
            </details>
          )}

          {/* DRIVE PREVIEW — collapsed by default */}
          {!isPending && !isRejected && (
            <details className="kb-card p-5" data-testid="drive-preview-card">
              <summary className="cursor-pointer list-none flex items-center justify-between">
                <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted inline-flex items-center gap-1.5">
                  <FolderOpen className="w-3 h-3" /> DRIVE FILES
                </div>
                <ChevronRight className="w-4 h-4 text-kb-ink-muted" />
              </summary>
              <div className="mt-3">
                <DrivePreviewSidebar
                  hasDrive={googleStatus.has_drive}
                  onConnect={() => requestExtraScopes(SCOPE_DRIVE)}
                  workspaceId={idea.id}
                />
              </div>
            </details>
          )}

          {/* RESEARCH LIFECYCLE — now surfaced through a right-edge slide-out
              (see LifecycleSlideout below). The sidebar block is intentionally
              removed to reclaim vertical space. */}

          {/* PUBLIC SHARE LINKS moved above Drive per new right-panel order —
              the duplicate block that was here has been consolidated to keep
              the sidebar tidy. */}

          {/* TEAM card removed — participants are now inside MENTOR & COLLABORATORS */}

          {/* STAGE HISTORY — shows 2 most recent entries as preview, expand for full list */}
          <details className="kb-card p-5" data-testid="stage-history-card">
            <summary className="cursor-pointer list-none">
              <div className="flex items-center justify-between">
                <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted">STAGE HISTORY</div>
                <ChevronRight className="w-4 h-4 text-kb-ink-muted" />
              </div>
              {(idea.stage_history || []).slice(-2).reverse().length > 0 && (
                <ol className="mt-3 space-y-2">
                  {(idea.stage_history || []).slice(-2).reverse().map((h, i) => (
                    <li key={i} className="flex gap-2.5 text-xs">
                      <FileText className="w-3 h-3 text-kb-ink-light mt-0.5 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="text-kb-ink truncate"><span className="font-medium">{h.stage}</span></div>
                        <div className="kb-mono text-[9px] text-kb-ink-light">{new Date(h.at).toLocaleDateString()}</div>
                      </div>
                    </li>
                  ))}
                </ol>
              )}
            </summary>
            <ol className="mt-3 space-y-3 pt-3 border-t border-slate-200">
              {(idea.stage_history || []).slice(-12).reverse().map((h, i) => (
                <li key={i} className="flex gap-2.5 text-xs">
                  <FileText className="w-3 h-3 text-kb-ink-light mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <div className="text-kb-ink"><span className="font-medium">{h.stage}</span> {h.by && <span className="text-kb-ink-muted">· {h.by}</span>}</div>
                    {h.note && <div className="text-kb-ink-muted line-clamp-2">{h.note}</div>}
                    <div className="kb-mono text-[9px] text-kb-ink-light mt-0.5">{new Date(h.at).toLocaleString()}</div>
                  </div>
                </li>
              ))}
            </ol>
          </details>
        </div>
      </div>

      {drawerStage && (
        <StageDetailDrawer ideaId={idea.id} stage={drawerStage} onClose={() => setDrawerStage(null)} onRefresh={refreshTimeline} />
      )}

      {/* RESEARCH LIFECYCLE — floating right-edge button + slide-out drawer
          reclaims sidebar space while keeping the timeline one tap away. */}
      {timeline && (
        <LifecycleSlideout
          timeline={timeline}
          open={lifecycleOpen}
          onOpen={() => setLifecycleOpen(true)}
          onClose={() => setLifecycleOpen(false)}
          onPickStage={(s) => { setLifecycleOpen(false); setDrawerStage(s); }}
        />
      )}
      {/* CALL PANELS are rendered globally via <PersistentCallHost /> so they
          survive navigation across pages. */}
    </div>
  );
}


// ---- Right-edge floating lifecycle drawer ---------------------------------
function LifecycleSlideout({ timeline, open, onOpen, onClose, onPickStage }) {
  const active = timeline.stages.find((s) => s.state === "active") || timeline.stages[0];
  // Overall progress = tasks completed across the WHOLE lifecycle, not just
  // the active stage. This is what the pinned edge-button now surfaces so a
  // glance tells you where the whole project sits (0-100%).
  const totals = timeline.stages.reduce((acc, s) => {
    const total = s.milestones.length;
    const done = s.milestones.filter((m) => m.status === "completed").length;
    acc.total += total; acc.done += done;
    return acc;
  }, { total: 0, done: 0 });
  const overallPct = totals.total > 0 ? Math.round((totals.done / totals.total) * 100) : 0;
  return (
    <>
      {/* Floating button — pinned to the right edge, always visible */}
      <button
        type="button"
        onClick={onOpen}
        className="fixed right-0 top-1/2 -translate-y-1/2 z-[100] bg-kb-primary text-white kb-mono text-[10px] uppercase tracking-[0.2em] px-2 py-4 rounded-l-sm shadow-lg hover:bg-kb-ink transition-colors"
        style={{ writingMode: "vertical-rl" }}
        data-testid="lifecycle-slideout-toggle"
        title={`Overall lifecycle progress — active stage: ${active?.stage} (${active?.progress_pct ?? 0}%)`}
      >
        Lifecycle · {overallPct}%
      </button>

      {/* Backdrop + panel */}
      <div className={`fixed inset-0 z-[110] transition-opacity ${open ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"}`} onClick={onClose}>
        <div className="absolute inset-0 bg-black/30" />
        <aside
          className={`absolute top-0 right-0 h-full w-[380px] max-w-[92vw] bg-white border-l border-slate-200 shadow-2xl transition-transform overflow-y-auto ${open ? "translate-x-0" : "translate-x-full"}`}
          onClick={(e) => e.stopPropagation()}
          data-testid="lifecycle-slideout-panel"
        >
          <div className="sticky top-0 bg-white border-b border-slate-200 flex items-center justify-between px-5 py-3">
            <div>
              <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted">RESEARCH LIFECYCLE</div>
              <div className="font-serif text-lg text-kb-ink mt-0.5">Timeline</div>
            </div>
            <button type="button" onClick={onClose} className="p-1.5 text-kb-ink-muted hover:text-kb-ink" data-testid="lifecycle-slideout-close">
              <ChevronRight className="w-5 h-5 rotate-180" />
            </button>
          </div>
          <div className="p-5 space-y-2">
            {timeline.stages.map((s, i) => {
              const stateColor = s.state === "done" ? "border-emerald-300 bg-emerald-50/30" :
                                 s.state === "active" ? "border-kb-primary bg-kb-primary/5" :
                                 "border-slate-200 bg-white";
              return (
                <button key={s.stage} onClick={() => onPickStage(s)}
                  className={`w-full text-left border rounded-sm p-3 transition-all hover:border-kb-primary group ${stateColor}`}
                  data-testid={`lifecycle-slideout-stage-${i}`}>
                  <div className="flex items-center gap-3">
                    <div className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 ${
                      s.state === "done" ? "bg-emerald-500 text-white" :
                      s.state === "active" ? "bg-kb-primary text-white" :
                      "bg-white border border-slate-300 text-slate-400"
                    }`}>
                      {s.state === "done" ? <CheckCircle2 className="w-3.5 h-3.5" /> :
                       s.state === "active" ? <Clock className="w-3.5 h-3.5" /> :
                       <span className="kb-mono text-[10px]">{String(i+1).padStart(2,'0')}</span>}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-serif text-sm text-kb-ink truncate">{s.stage}</div>
                      <div className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted">
                        {s.milestones.filter((m) => m.status === "completed").length}/{s.milestones.length} tasks · {s.progress_pct}%
                      </div>
                    </div>
                    <ChevronRight className="w-4 h-4 text-kb-ink-light group-hover:text-kb-primary" />
                  </div>
                </button>
              );
            })}
          </div>
        </aside>
      </div>
    </>
  );
}
